import { gql } from '@apollo/client';
import { insuranceFragment, hotelFragment } from 'store/fragments';

export const booking = `
  detailsCaptured
  language
  subscriptionVATHasBeenCancelled
  wasPurchasedWithSubscription
  isHomogeneous
  paymentsRefundBalance
  salesChannelCode
  additionalDetails {
    value
    key
    itemRef
    passengerRef
  }
  outboundExtras {
    ...product
    leg {
      serviceName
      serviceType {
        code
      }
      serviceIdentifier
      timing {
        isoDate
        departs
      }
      origin {
        name
        shortCode
      }
      destination {
        name
        shortCode
      }
    }
  }
  inboundExtras {
    ...product
    leg {
      serviceName
      serviceType {
        code
      }
      serviceIdentifier
      timing {
        isoDate
        departs
      }
      origin {
        name
        shortCode
      }
      destination {
        name
        shortCode
      }
    }
  }
  provisionalOfflineFees: fees(provisional: true) {
    code
    price
    cancelled
  }
  isLoyalty
  totalPriceToBePaid
  agent {
    contractCode
  }
  outboundOrigin {
    uic
    name
  }
  outboundDestination {
    uic
    name
  }
  inboundOrigin {
    uic
    name
  }
  inboundDestination {
    uic
    name
  }
  productFamily
  productFamilyGroup
  isDOBRequired
  isPackage
  isGroupBooking
  nonExchangedOutbound : outbound(hideExchanged: true) {
    id
  }
  nonExchangedInbound : inbound(hideExchanged: true) {
    id
  }
  hotels {
    ...CompleteHotel
  }
  insurances {
    ...CompleteInsurance
  }
  notes {
    uuid
    content
    author
    createdAt
  }
  fees {
    name
    code
    price
    provisional
    currency
    itemRef
    cancelled
  }
  payments {
    id
    uuid
    refId
    currency
    currencyRate
    reference
    externalReference
    refundAvailable
    provider
    method
    amount
    availableRefundAmount
    transactionTimestamp
    refundProcessTimestamp
    paymentStatus
    createdRevision
    isPayout
    isBalance
    isSuccessful
    action @client
    display @client
  }
  discounts: vouchers(class: DISCOUNT) {
    code
    alias
    class
    value
    currency
    paymentId
    isActive
  }
  travelPassProducts {
    travelDate
    startDate
    endDate
    name
    code
    price
    currency
    itemRef
    type
    passenger {
      id
      firstName
      lastName
    }
    cancelled
    provisional
    canBeCancelled
    canBeRefunded
    productFamilyId
  }
  subscriptions: travelPassProducts {
    travelDate
    startDate
    endDate
    name
    code
    price
    currency
    itemRef
    type
    passenger {
      id
      firstName
      lastName
    }
    cancelled
    provisional
    canBeCancelled
    canBeRefunded
    productFamilyId
  }
  refunds @client {
    id
    refId
    currency
    currencyRate
    reference
    externalReference
    refundAvailable
    provider
    method
    amount
    transactionTimestamp
    refundProcessTimestamp
    paymentStatus
    action @client
    display @client
  }
  provisionalPayments : payments(onlyProvisional: true) {
    id
    currency
    method
    amount
  }
  amountDue {
    amount
    currency
  }
  reference
  status
  displayStatus @client
  arePassengersEditable @client
  allBookingItems @client {
    ...product
    status
  }
  expiryTimestamp
  changeExpiryTimestamp
  isInAfterSales
  wasPurchasedWithSubscription
  passengers {
    ...passenger
  }
  customer {
    firstName
    lastName
    email
    companyName
    phoneNumber
    unformattedPhoneNumber {
      countryCode
      number
    }
    address {
      street
      postalCode
      city
      countryCode
    }
  }
  currency
  productFamilies {
    code
    name
    familyGroup
  }
  outboundTotalPrice
  inboundTotalPrice
  totalPrice
  outbound {
    ...leg
  }
  inbound {
    ...leg
  }
  eurostarOutbound @client {
    ...leg
  }
  eurostarInbound @client {
    ...leg
  }
  greenspeedOutbound @client {
    ...leg
  }
  greenspeedInbound @client {
    ...leg
  }
  onHold {
    depositAmount
  }
  isMigratedMulti
  isMultiCurrency
  extraOnlyParentBookingReference
  extraOnlyBookingReference
  updatedTimestamp
  createTimestamp
  confirmedTimestamp
  cancelledTimestamp
  agent {
    organization
    office
    username
  }
  pendingCancellationLegs {
    ...leg
  }
  pendingCreationLegs {
    ...leg
  }
  externalBookingReferences {
    source
    externalId
  }
  latestRevisionSource {
    username
}
`;
export const leg = `
  fragment leg on Leg {
    ...legFields
    addOnLeg {
      ...legFields
    }
  }

  fragment legFields on Leg {
    isThroughFare
    isSummatedFare
    cancelled
    isMultiInventory
    direction
    serviceIdentifier
    trainNumber
    id
    uuid
    ticketDocuments {
      pin
    }
    classOfService {
      code
      name
      showNewTrainMapName
    }
    extraProducts {
      ...product
    }
    origin {
      name
      uic
      shortCode
      timezone
      countryCode
      isPseudoStation
    }
    destination {
      name
      uic
      shortCode
      countryCode
      isPseudoStation
    }
    timing {
      isoDate
      date
      departs
      arrives
      arrivalDate
    }
    fees {
      name
      code
      price
      provisional
      cancelled
      currency
      itemRef
    }
		feeProducts {
      passengerId
      code
      isCancelled
      tariffDetails {
        tariffCode
      }
    }
    canBeUpgraded
    products {
      ...product
    }
    activeBookingItems @client {
      ...product
    }

    bookingItems @client {
      ...product
      mealCode
      status
      isSeatEditable
      isMealEditable
    }

    temporarilyCancelledBookingItems @client {
      ...product
      mealCode
      status
    }

    seats {
      coach
      number
      inventoryClass
      itemRef
      passenger {
        id
        type
      }
    }
    meals {
      code
      passenger {
        id
      }
      price
      name
    }
    productSummaries @client {
      name
      passengerType
      code
      total
      quantity
      price
      currency
    }
    refundSummaries @client {
      name
      passengerType
      code
      total
      quantity
      price
    }
    serviceType {
      name
      code
      brandCode
    }
  }
`;

export const passenger = `
  fragment passenger on Passenger {
    id
    refId
    uuid
    type
    firstName
    lastName
    email
    phoneNumber
    unformattedPhoneNumber {
      countryCode
      number
    }
    birthDate
    loyaltyNumber
    disabilityType
    discountCards {
      code
      identifier
      name
      requiresPassengerIdentifier
    }
    travelDocumentsRequired {
      travelDocumentType
      required
    }
  }
`;

export const product = `
  fragment product on Product {
    uuid
    origin {
      uic
      name
    }
    destination {
      uic
      name
    }
    itemId
    itemRef
    associatedItemRefs
    passengerId
    isAFareAddOn
    isCheckedIn
    isExpired
    isRebookOFEActive
    isUKEntryDenied
    loyaltyPoints
    price
    currency
    tariffDetails {
      tariffCode
      bucketCode
    }
    confirmedTimestamp
    voidTimestamp
    expiryTimestamp
    currency
    code
    inventoryClass
    name
    cancelled
    provisional
    isSuspended
    isPlaceholder
    externalTicketNumber
    externalProductReference
    canBeRebooked
    canBeCancelled
    canBeRefunded
    canChangeSeat
    canChangeMeal
    canChangeName
    afterSalesRules {
      active
      validUntil
      override
      maintainPrice
      price
      type
      currentActive
    }
    passenger {
      ...passenger
    }
    productFamily {
      code
      name
      familyGroup
    }
    type
    additionalDetails {
      key
      value
    }
    ticketNumber
  }
`;

export default gql`
    query booking($reference: String!) {
        booking(reference: $reference) {
            ${booking}
        }
    }

    ${insuranceFragment}

    ${hotelFragment}

    ${leg}

    ${passenger}

    ${product}
`;
